/**
 * NewWorldDialog — name, seed, and the generation editor.
 *
 * IT IS A SHELL NOW. It used to be 557 lines: twelve `Field<T>` arrays, 74 sliders, 11 pills, a
 * bespoke slider row, pill, input, subheading and curve editor, and an `&&` chain in front of every
 * section duplicating the gate its pill already expressed. Every one of those grew by hand with
 * every feature, and between them they reached 74 of ~116 tunable fields. The form is generated
 * from the node catalog now (GenConfigEditor), so this file has no opinion about what a world
 * contains and stops growing when a kind is added.
 *
 * Settings persist to localStorage so they carry across sessions (tweak, create, tweak); name and
 * seed are always fresh.
 */

import { useEffect, useRef, useState } from 'react';
import { defaultGenConfig, GEN_CONFIG_VERSION, parseGenConfig, type WorldGenConfig } from '@worldify/shared';
import { nextWorldName, randomWorldSeed } from '../game/world/WorldManager';
import { GenConfigEditor } from './GenConfigEditor';
import { ACCENT_BG, ACCENT_ON, MOTION, PanelRow, PanelTextField, Sheet } from './kit';

interface NewWorldDialogProps {
  onCancel: () => void;
  onCreate: (name: string, seed: number, genConfig: WorldGenConfig) => void;
}

/** v8: `base`/`flat` left, `scatter` merged into `meadow`, `wild` arrived, and the two datums became
 *  one. Old saves are not migrated (plan decision 1) — a new key starts everyone on the defaults
 *  rather than merging a stale shape, and a v1 config read forward would silently turn a flat world
 *  into a landform one. */
const STORE_KEY = 'worldify-new-world-v8';

/**
 * A FRESH dialog opens on the CATALOG's own defaults — every tickbox ticked — and there is no
 * second set of them here. It used to force flat ground and regions on, which meant two answers to
 * "what does a new world look like" and the catalog's was the one nobody saw.
 *
 * Note the one place the dialog and the ENGINE still differ, deliberately: `new TerrainGenerator({
 * seed })` with no config is a region-free landform world, because that is what every checksum
 * guard in the repo baselines. The dialog's default and the engine's default are different
 * questions; resolveConfig.test.ts pins the difference so it cannot widen unnoticed.
 */
function loadSaved(): WorldGenConfig {
  const seed = randomWorldSeed();
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const stored = JSON.parse(raw) as Partial<WorldGenConfig>;
      if (stored?.version === GEN_CONFIG_VERSION) return parseGenConfig(stored, seed);
    }
  } catch { /* ignore corrupt/absent */ }
  return defaultGenConfig(seed);
}

export function NewWorldDialog({ onCancel, onCreate }: NewWorldDialogProps) {
  const [name, setName] = useState('');
  const [seed, setSeed] = useState(() => String(randomWorldSeed()));
  const [cfg, setCfg] = useState<WorldGenConfig>(() => loadSaved());
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    nextWorldName().then(setName);
    // Deliberately NOT auto-focusing the name field: on mobile that pops the on-screen keyboard the
    // moment the dialog opens. Name/seed are pre-filled; the user taps a field only to edit it.
    return () => { if (copyTimer.current) clearTimeout(copyTimer.current); };
  }, []);

  const submit = () => {
    const parsed = parseInt(seed, 10);
    const finalSeed = Number.isFinite(parsed) ? parsed : randomWorldSeed();
    try { localStorage.setItem(STORE_KEY, JSON.stringify(cfg)); } catch { /* ignore */ }
    onCreate(name.trim(), finalSeed, { ...cfg, seed: finalSeed });
  };

  /** Copy the dialled-in settings, so a world you like becomes the next set of engine defaults. */
  const exportSettings = async () => {
    const json = JSON.stringify(cfg, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy your generation settings:', json);
    }
  };

  const btn = (primary: boolean) =>
    `px-4 h-9 rounded-full text-xs font-bold cursor-pointer ${MOTION} ${
      primary ? `${ACCENT_BG} ${ACCENT_ON} hover:brightness-105`
        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
    }`;

  return (
    <Sheet
      title="New World"
      onClose={onCancel}
      footer={
        <>
          <button className={`${btn(false)} mr-auto`} onClick={exportSettings} title="Copy these settings to the clipboard">
            {copied ? 'Copied!' : 'Export'}
          </button>
          <button className={btn(false)} onClick={onCancel}>Cancel</button>
          <button className={btn(true)} onClick={submit}>Create</button>
        </>
      }
    >
      <div className="flex flex-col gap-2 pb-2">
        <PanelRow label="Name">
          <PanelTextField value={name} onChange={setName} onEnter={submit} maxLength={40} ariaLabel="World name" />
        </PanelRow>
        <PanelRow label="Seed">
          <PanelTextField
            value={seed}
            onChange={(v) => setSeed(v.replace(/[^0-9]/g, ''))}
            onEnter={submit}
            inputMode="numeric"
            action={{ label: 'Random', onClick: () => setSeed(String(randomWorldSeed())) }}
            ariaLabel="World seed"
          />
        </PanelRow>
      </div>

      <GenConfigEditor value={cfg} onChange={setCfg} />
    </Sheet>
  );
}
